/**
 * Custom cart drawer – AJAX cart updates with event-driven dynamic rendering.
 * Uses Shopify Cart API with bundled section rendering and custom events for updates.
 */
(function () {
  const drawer = document.getElementById('custom-cart-drawer');
  if (!drawer) return;

  const panel = drawer.querySelector('.custom-cart-drawer-panel');

  // Get section ID from data attribute or from parent shopify-section element
  let sectionId = drawer.getAttribute('data-section-id');
  if (!sectionId) {
    const shopifySection = drawer.closest('[id^="shopify-section-"]');
    if (shopifySection) {
      sectionId = shopifySection.id.replace('shopify-section-', '');
    }
  }

  // Get section identifier for Cart API
  // According to Shopify docs, sections parameter uses the section ID directly
  // The response wraps it in <div id="shopify-section-{id}"> but we request just the ID
  const sectionIdentifier = sectionId; // Use section ID directly, not prefixed
  let isUpdating = false;
  let updatingItemKey = null;

  /**
   * Custom event dispatcher for cart updates
   */
  function dispatchCartUpdate(cart) {
    const event = new CustomEvent('cart:updated', {
      detail: { cart },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Update cart count in header
   */
  function updateCartCount(count) {
    const headerTitle = drawer.querySelector('[data-cart-count]');
    if (headerTitle) {
      headerTitle.textContent = `Shopping Bag (${count})`;
    }
    // Also update cart count in header if it exists
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
      const countBadge = trigger.querySelector('[data-header-cart-count]');
      if (countBadge) {
        countBadge.textContent = count > 0 ? `(${count})` : '(0)';
      }
    });
  }

  /**
   * Update shipping bar dynamically
   */
  function updateShippingBar(cart) {
    const shippingBar = drawer.querySelector('[data-shipping-bar]');
    if (!shippingBar) return;

    const threshold = parseInt(shippingBar.dataset.threshold || '15000', 10);
    const cartSubtotal = cart.items_subtotal_price || 0;
    const remaining = Math.max(0, threshold - cartSubtotal);
    const progressPercent = Math.min(100, Math.max(1, (cartSubtotal / threshold) * 100));
    const qualifies = cartSubtotal >= threshold;

    const messageEl = shippingBar.querySelector('[data-shipping-message]');
    if (messageEl) {
      if (qualifies) {
        messageEl.innerHTML = 'Congratulations! Your order qualifies for <b>free shipping!</b>';
      } else {
        const remainingFormatted = formatMoney(remaining);
        messageEl.textContent = `You're ${remainingFormatted} away from free shipping!`;
      }
    }

    const progressEl = shippingBar.querySelector('[data-shipping-progress]');
    if (progressEl) {
      progressEl.style.width = `${progressPercent}%`;
    }
  }

  /**
   * Update cart footer (subtotal, savings)
   */
  function updateCartFooter(cart) {
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.items_subtotal_price || 0);
    }

    const threshold = parseInt(drawer.querySelector('[data-shipping-bar]')?.dataset.threshold || '15000', 10);
    const qualifies = (cart.items_subtotal_price || 0) >= threshold;
    const hasDiscounts = (cart.total_discount || 0) > 0;
    const showSavings = hasDiscounts || qualifies;

    const savingsLine = drawer.querySelector('[data-cart-savings]');
    if (savingsLine) {
      if (showSavings) {
        savingsLine.classList.remove('tw:hidden!');
      } else {
        savingsLine.classList.add('tw:hidden!');
      }
    }

    const discountsEl = drawer.querySelector('[data-cart-discounts]');
    if (discountsEl) {
      if (hasDiscounts) {
        discountsEl.textContent = formatMoney(cart.total_discount);
        discountsEl.style.display = '';
      } else {
        discountsEl.style.display = 'none';
      }
    }

    const badgeEl = drawer.querySelector('[data-free-shipping-badge]');
    if (badgeEl) {
      if (qualifies) {
        badgeEl.classList.remove('tw:hidden!');
      } else {
        badgeEl.classList.add('tw:hidden!');
      }
    }
  }

  /**
   * Format money using Shopify's money format
   */
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      let moneyFormat = '{{amount}}';
      if (window.theme && window.theme.moneyFormat) {
        moneyFormat = window.theme.moneyFormat;
      } else if (typeof shop !== 'undefined' && shop.money_format) {
        moneyFormat = shop.money_format;
      }
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Set loading state on cart item
   */
  function setItemLoading(itemKey, isLoading) {
    const item = drawer.querySelector(`[data-item-key="${itemKey}"]`)?.closest('li');
    if (!item) return;

    if (isLoading) {
      item.classList.add('tw:opacity-50', 'tw:pointer-events-none');
      item.style.pointerEvents = 'none';
    } else {
      item.classList.remove('tw:opacity-50', 'tw:pointer-events-none');
      item.style.pointerEvents = '';
    }
  }

  function openDrawer() {
    drawer.classList.add('is-open');
    if (panel) panel.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    if (panel) panel.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  drawer.querySelectorAll('[data-drawer-close]').forEach((el) => {
    el.addEventListener('click', closeDrawer);
  });

  document.querySelectorAll('[data-cart-drawer-trigger]').forEach((btn) => {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (drawer.classList.contains('is-open')) closeDrawer();
      else openDrawer();
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  /**
   * Add item to cart using Cart API with bundled section rendering
   */
  async function addToCart(formData, openDrawerAfterAdd = true) {
    if (isUpdating) return;
    isUpdating = true;

    try {
      // Convert FormData to object for JSON serialization
      const data = {};
      for (const [key, value] of formData.entries()) {
        if (key === 'id') {
          data.id = parseInt(value, 10);
        } else if (key === 'quantity') {
          data.quantity = parseInt(value, 10) || 1;
        } else {
          data[key] = value;
        }
      }

      // Add item to cart
      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || `Failed to add item: ${addResponse.status}`);
      }

      const lineItem = await addResponse.json();
      console.log('Item added to cart:', lineItem);

      await new Promise((resolve) => setTimeout(resolve, 500));

      let cartResult;
      let cart;
      let sections;
      let retries = 0;
      const maxRetries = 3;

      while (retries < maxRetries) {
        const updatePayload = {
          updates: {},
          sections: sectionIdentifier,
        };

        const cartResponse = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
          },
          body: JSON.stringify(updatePayload),
        });

        if (!cartResponse.ok) {
          throw new Error(`Failed to fetch cart with sections: ${cartResponse.status}`);
        }

        cartResult = await cartResponse.json();

        const extracted = { ...cartResult };
        sections = extracted.sections;
        delete extracted.sections;
        cart = extracted;

        console.log('Cart fetched with sections:', {
          itemCount: cart.item_count,
          itemsLength: cart.items?.length,
          hasSections: !!sections,
          sectionKeys: sections ? Object.keys(sections) : [],
          sectionIdentifier,
          retryAttempt: retries + 1,
        });

        // If cart has items and sections, break out of retry loop
        if (cart.item_count > 0 && cart.items && cart.items.length > 0 && sections) {
          break;
        }

        // If cart is still empty or sections missing, wait and retry
        if (retries < maxRetries - 1) {
          const reason = !cart.item_count ? 'cart appears empty' : !sections ? 'sections missing' : 'items missing';
          console.log(`${reason}, retrying in 1 second... (attempt ${retries + 1}/${maxRetries})`);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          retries++;
        } else {
          console.warn('Cart or sections still missing after retries, proceeding anyway');
          break;
        }
      }

      dispatchCartUpdate(cart);

      // Update drawer content from section rendering
      if (sections && sectionIdentifier) {
        const sectionHTML = sections[sectionIdentifier] || sections[sectionId] || Object.values(sections)[0];

        if (sectionHTML) {
          console.log('Updating drawer from section rendering', {
            sectionIdentifier,
            sectionId,
            cartItemCount: cart.item_count,
            sectionHTMLLength: sectionHTML.length,
            availableSectionKeys: Object.keys(sections),
          });
          const updateSuccess = updateDrawerFromSection(sectionHTML, cart);

          // If section rendering failed (stale empty state), try one more time with longer delay
          if (updateSuccess === false && cart.item_count > 0) {
            console.log('Section rendering showed stale data, retrying with longer delay...');
            await new Promise((resolve) => setTimeout(resolve, 2000)); // Additional 2 second delay

            // Retry using /cart/update.js with sections
            const retryPayload = {
              updates: {},
              sections: sectionIdentifier,
            };
            const retryCartResponse = await fetch(`${Shopify.routes.root}cart/update.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
              },
              body: JSON.stringify(retryPayload),
            });

            if (retryCartResponse.ok) {
              const retryCartResult = await retryCartResponse.json();
              const retryExtracted = { ...retryCartResult };
              const retrySections = retryExtracted.sections;
              delete retryExtracted.sections;
              const retryCart = retryExtracted;

              const retrySectionHTML =
                retrySections?.[sectionIdentifier] ||
                retrySections?.[sectionId] ||
                Object.values(retrySections || {})[0];

              if (retrySectionHTML) {
                console.log('Retrying section rendering update...');
                const retrySuccess = updateDrawerFromSection(retrySectionHTML, retryCart);
                if (retrySuccess === false) {
                  console.warn('Section rendering still stale after retry, updating counts only');
                  updateCartCount(cart.item_count || 0);
                  updateShippingBar(cart);
                  updateCartFooter(cart);
                }
              } else {
                console.warn('No section HTML in retry, updating counts only');
                updateCartCount(cart.item_count || 0);
                updateShippingBar(cart);
                updateCartFooter(cart);
              }
            } else {
              console.warn('Retry fetch failed, updating counts only');
              updateCartCount(cart.item_count || 0);
              updateShippingBar(cart);
              updateCartFooter(cart);
            }
          }
        } else {
          console.warn('Section HTML not found in sections object', {
            availableKeys: Object.keys(sections),
            sectionIdentifier,
            sectionId,
          });
          // Fallback: update elements directly if section rendering failed
          updateCartCount(cart.item_count || 0);
          updateShippingBar(cart);
          updateCartFooter(cart);
        }
      } else {
        console.warn('No sections in response, trying Section Rendering API directly...', {
          hasSections: !!sections,
          sectionIdentifier,
        });

        // Fallback: Try Section Rendering API directly
        if (sectionIdentifier && cart.item_count > 0) {
          try {
            const sectionResponse = await fetch(`${Shopify.routes.root}sections/${sectionIdentifier}`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            });

            if (sectionResponse.ok) {
              const sectionData = await sectionResponse.json();
              if (sectionData.section && sectionData.section.html) {
                console.log('Got section HTML from Section Rendering API');
                const updateSuccess = updateDrawerFromSection(sectionData.section.html, cart);
                if (updateSuccess !== false) {
                  // Success - sections updated
                  return { lineItem, cart };
                }
              }
            }
          } catch (error) {
            console.warn('Section Rendering API fallback failed:', error);
          }
        }

        // Final fallback: update manually
        console.warn('Updating manually without section rendering');
        updateCartCount(cart.item_count || 0);
        updateShippingBar(cart);
        updateCartFooter(cart);
      }

      // Open drawer after successful add
      if (openDrawerAfterAdd) {
        openDrawer();
      }

      return { lineItem, cart };
    } catch (error) {
      console.error('Add to cart error:', error);
      alert(error.message || 'Unable to add item to cart. Please try again.');
      throw error;
    } finally {
      isUpdating = false;
    }
  }

  /**
   * Update cart using Cart API with bundled section rendering
   */
  async function updateCart(data, itemKey = null) {
    if (isUpdating) return;
    isUpdating = true;
    updatingItemKey = itemKey;

    if (itemKey) {
      setItemLoading(itemKey, true);
    }

    try {
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          sections: sectionIdentifier,
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const result = await response.json();
      dispatchCartUpdate(result);

      // Update drawer content from section rendering
      // Shopify returns sections with keys matching the section IDs requested
      // According to Shopify docs, sections are rendered AFTER cart updates complete
      if (result.sections && sectionIdentifier) {
        // Try multiple possible keys: the exact identifier, base sectionId, or any matching key
        const sectionHTML =
          result.sections[sectionIdentifier] || result.sections[sectionId] || Object.values(result.sections)[0];

        if (sectionHTML) {
          updateDrawerFromSection(sectionHTML, result);
        } else {
          // Fallback: update elements directly if section rendering failed
          updateCartCount(result.item_count || 0);
          updateShippingBar(result);
          updateCartFooter(result);
        }
      } else {
        // No section rendering available
        updateCartCount(result.item_count || 0);
        updateShippingBar(result);
        updateCartFooter(result);
      }

      return result;
    } catch (error) {
      console.error('Cart update error:', error);
      alert('Unable to update cart. Please try again.');
    } finally {
      isUpdating = false;
      updatingItemKey = null;
      if (itemKey) {
        setItemLoading(itemKey, false);
      }
    }
  }

  /**
   * Update drawer content from section HTML
   * Uses Shopify's bundled section rendering - replaces entire [data-cart-content] container
   * According to Shopify docs: sections are wrapped in <div id="shopify-section-{id}">
   */
  function updateDrawerFromSection(sectionHTML, cart) {
    console.log('updateDrawerFromSection called', { sectionIdentifier, sectionId, cartItemCount: cart.item_count });

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sectionHTML;

    // Shopify wraps sections in a div with id="shopify-section-{id}"
    // The section HTML structure is: <div id="shopify-section-{id}"><div id="custom-cart-drawer">...</div></div>
    const sectionWrapper =
      tempDiv.querySelector(`#shopify-section-${sectionIdentifier}`) ||
      tempDiv.querySelector(`#shopify-section-${sectionId}`) ||
      tempDiv.querySelector(`#${sectionIdentifier}`) ||
      tempDiv.querySelector('[id^="shopify-section-"]');

    console.log('Section wrapper found:', !!sectionWrapper);

    const newDrawer =
      sectionWrapper?.querySelector('#custom-cart-drawer') || tempDiv.querySelector('#custom-cart-drawer');

    if (!newDrawer) {
      console.warn('Could not find cart drawer in section HTML', {
        sectionHTMLLength: sectionHTML.length,
        tempDivHTML: tempDiv.innerHTML.substring(0, 500),
      });
      return false;
    }

    const wasOpen = drawer.classList.contains('is-open');

    // Replace the entire [data-cart-content] container using innerHTML
    // This ensures complete replacement of all content including items and footer
    // Shopify's section rendering handles empty vs non-empty states automatically
    const oldContent = drawer.querySelector('[data-cart-content]');
    const newContent = newDrawer.querySelector('[data-cart-content]');

    if (!oldContent) {
      console.error('Old cart content container not found');
      return false;
    }

    if (!newContent) {
      console.error('New cart content container not found in section HTML', {
        newDrawerHTML: newDrawer.innerHTML.substring(0, 500),
      });
      return false;
    }

    // Verify the section HTML actually contains cart items (not empty state)
    // Check for cart item indicators in the rendered HTML
    const hasCartItems =
      newContent.querySelector('[data-item-key]') ||
      newContent.querySelector('li[data-cart-item]') ||
      (cart.item_count > 0 && !newContent.textContent.includes('Your next wig is waiting'));

    const hasEmptyState =
      newContent.textContent.includes('Your next wig is waiting') || newContent.querySelector('[data-empty-cart]');

    console.log('Replacing cart content', {
      oldContentLength: oldContent.innerHTML.length,
      newContentLength: newContent.innerHTML.length,
      cartItemCount: cart.item_count,
      hasCartItems,
      hasEmptyState,
      sectionHTMLPreview: newContent.innerHTML.substring(0, 300),
    });

    // If cart should have items but section shows empty state, don't replace with stale HTML
    if (cart.item_count > 0 && hasEmptyState && !hasCartItems) {
      console.warn('WARNING: Cart has items but section HTML shows empty state. Section rendering appears stale.');
      console.warn('Skipping section HTML replacement. Cart data will be updated manually.');
      // Don't replace content - return early and let the caller handle manual update
      return false;
    }

    // Use innerHTML replacement for guaranteed complete replacement
    // This replaces shipping bar, items container (with empty state or items), and footer
    oldContent.innerHTML = newContent.innerHTML;

    console.log('Cart drawer updated successfully');
  }

  /**
   * Re-initialize event listeners after content replacement
   */
  function reinitializeEventListeners() {
    drawer.querySelectorAll('[data-drawer-close]').forEach((el) => {
      el.removeEventListener('click', closeDrawer);
      el.addEventListener('click', closeDrawer);
    });

    drawer.querySelectorAll('[data-cart-remove]').forEach((btn) => {
      btn.removeEventListener('click', handleRemoveItem);
      btn.addEventListener('click', handleRemoveItem);
    });

    const form = drawer.querySelector('[data-cart-form]');
    if (form) {
      form.removeEventListener('submit', handleFormSubmit);
      form.addEventListener('submit', handleFormSubmit);
    }

    const checkoutLink = drawer.querySelector('a[href*="/cart"]');
    if (checkoutLink && checkoutLink.textContent.trim().toLowerCase().includes('secure checkout')) {
      checkoutLink.removeEventListener('click', handleCheckoutClick);
      checkoutLink.addEventListener('click', handleCheckoutClick);
    }
  }

  /**
   * Handle remove item
   */
  function handleRemoveItem(e) {
    const btn = e.currentTarget;
    const itemKey = btn.getAttribute('data-item-key');
    if (!itemKey) return;

    updateCart({ updates: { [itemKey]: 0 } }, itemKey);
  }

  /**
   * Handle form submission
   */
  function handleFormSubmit(e) {
    e.preventDefault();
  }

  /**
   * Handle checkout link click
   */
  function handleCheckoutClick(e) {
    closeDrawer();
  }

  /**
   * Listen for cart updates from other sources
   */
  document.addEventListener('cart:updated', function (e) {
    const cart = e.detail.cart;
    updateCartCount(cart.item_count || 0);
    updateShippingBar(cart);
    updateCartFooter(cart);
  });

  // Initialize event listeners on page load
  reinitializeEventListeners();

  // Expose addToCart function globally for use by product forms
  window.CartDrawer = {
    addToCart,
    openDrawer,
    closeDrawer,
    updateCart,
  };
})();
